"use client";
import React, { useMemo, useEffect, useState } from "react";
import { useMediaQuery } from '@mui/material';
import '../../assets/styles/scss/_userDeal.scss'
import awsconfig from '../../awsconfig';
import { formatPrice, formatDealTime } from "../../utils";
import { fetchAuthSession, confirmSignIn, signIn } from 'aws-amplify/auth';
import { useSearchParams } from "next/navigation";
import UserDeals from "components/Deals/DealsView";
import { Amplify } from 'aws-amplify';
import { useConfig } from "components/ConfigProvider";
import { SecurityUtils } from 'pp-web'
import { usePathname } from 'next/navigation';
import CategoryChips from './CategoryChips';


// Creator-specific imports
import {
    Checkbox,
    MenuItem,
    Modal,
    TextField,
    Box,
    Button,
    Typography,
    Card,
    CardMedia,
    CardContent,
    Drawer,
    Popover,
    InputAdornment,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import { FaEye, FaPlus, FaSearch, FaTrash, FaPlay, FaCode, FaCopy, FaTimes } from 'react-icons/fa';
import * as PolnApi from "../../utils/api-service";
import { useDeals } from "app/providers/deals-provider";
import { useAuth } from "../../app/providers/auth-provider";

Amplify.configure(awsconfig);

export default function DealsContent() {
    const config = useConfig();
    const auth = useAuth()
    const pathname = usePathname();

    const {
        userType,
        collections,
        search,
        deals,
        setDealLoading,
        fetchDeals,
        handleViewMore,
        DEFAULT_COLLECTION,
        filters,
        collection,
        setCollection,
        selectedCollectionCategory
    } = useDeals()

    const [loading, setLoading] = useState(true);
    const searchParams = useSearchParams();
    const [userLocal, setUserLocal] = useState(null);
    const isMobile = useMediaQuery('(max-width:1024px)');
    const [user, setUser] = useState(null)

    // User-specific state
    const [isPreDealLoading, setPreDealLoading] = useState(false);

    // Creator-specific state
    const [selectedList, setSelectedList] = useState('');
    const [selectedDeals, setSelectedDeals] = useState([]);
    const [lists, setLists] = useState([]);
    const [isListsLoading, setIsListsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [isSavingList, setIsSavingList] = useState(false);
    const [deletingListName, setDeletingListName] = useState(null);
    const [viewListModal, setViewListModal] = useState(false);
    const [showSelectedSidebar, setShowSelectedSidebar] = useState(false);
    const [showHtmlCode, setShowHtmlCode] = useState(false);
    const [htmlCode, setHtmlCode] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchList, setSearchList] = useState('');
    const [filteredLists, setFilteredLists] = useState([]);
    const listMenuOpen = Boolean(anchorEl);
    const id = listMenuOpen ? 'list-popover' : undefined;

    // User authentication logic (for user type)

    useEffect(() => {
        if (auth && (auth.user && auth.user?.email)) {
            setUser(auth.user)
        }
    }, [auth])

    useEffect(() => {
        if (userType !== 'user') return;

        const signInAndConfirm = async (email, token) => {
            await signIn({
                username: email,
                options: {
                    authFlowType: "CUSTOM_WITHOUT_SRP",
                    preferredChallenge: "EMAIL_OTP"
                }
            });
            await confirmSignIn({
                challengeResponse: token
            });
        };

        const getCurrentUser = async () => {
            const session = await fetchAuthSession();
            return session.tokens?.idToken.payload;
        };

        const getUser = async () => {
            try {
                let currentUser = await getCurrentUser();
                const email = searchParams.get("email");
                const token = searchParams.get("token");
                if (!currentUser) {
                    if (email && token) {
                        await signInAndConfirm(email, token);
                        currentUser = await getCurrentUser();
                    } else {
                        // router.push('/landing/deals');
                        return;
                    }
                } else if (email !== currentUser.email && token) {
                    // Sign out and sign in with new credentials
                    await signInAndConfirm(email, token);
                    currentUser = await getCurrentUser();
                }
                return currentUser;
            } catch (error) {
                // Error handling for user fetching
            } finally {
                setLoading(false);
            }
        };

        const getUserDeals = async () => {
            const currentUser = await getUser();

            setPreDealLoading(true);

            if (currentUser) {
                setUserLocal(currentUser);
            }

            if (!search && !currentUser) {
                setCollection(DEFAULT_COLLECTION);
            }

            setPreDealLoading(false)
        }

        if (collections && config) {
            getUserDeals();
        }
    }, [collections, config, userType]);

    // Creator-specific logic
    useEffect(() => {
        if (userType === 'creator' && user) {
            getCreatorList();
        }
    }, [user, userType]);

    useEffect(() => {
        if (!collection) return;
        const _collections = selectedCollectionCategory ? [collection, selectedCollectionCategory] : [collection];
        fetchDeals(search || null, _collections, 1, filters, user);
    }, [user, userType, search, filters, selectedCollectionCategory]);

    // Common deal management functions
    const deleteDeal = async (productId, promoCode) => {
        if (!config) return;
        await fetch(`${config.siteApi.apiUrl}/crm/deal`, {
            method: 'PUT',
            body: JSON.stringify({
                productId,
                promoCode,
                delete: true
            })
        });
    }

    // Creator-specific functions
    const handleListMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        if (user) {
            getCreatorList();
        }
    };

    const handleListMenuClose = () => {
        setAnchorEl(null);
        setDeletingListName(null);
    };

    const isDealSelected = (dealId) => {
        return !!selectedDeals?.find(deal => deal.productId === dealId);
    };

    const filteredDeals = useMemo(() => {
        if (userType === 'user' || !deals || !selectedDeals) return deals || [];
        const selectedIds = selectedDeals.map(deal => deal.productId);
        return deals.filter(deal => !selectedIds.includes(deal.productId));
    }, [deals, selectedDeals, userType]);

    async function getCreatorList() {
        setIsListsLoading(true);
        let res = await PolnApi.request('/creator/lists')
        if (res?.statusCode === 200) {
            setLists(res?.body?.lists || []);
        }
        setIsListsLoading(false);
    }

    async function saveCreatorList(listName, productIds = [], promoCodes = []) {
        let res = await PolnApi.request('/creator/lists', {
            method: 'POST',
            body: {
                listName,
                productIds,
                promoCodes
            }
        });

        if (res?.statusCode === 200) {
            await getCreatorList();
        }
    }

    async function getUserProductsDataFromProductIds(listName) {
        let res = await PolnApi.request(`/creator/lists/${listName}`);

        if (res?.statusCode === 200) {
            return res?.body?.products || [];
        } else {
            return [];
        }
    }

    const handleListSelect = async (listName) => {
        setSelectedList(listName);
        setSelectedDeals([]);

        try {
            const products = await getUserProductsDataFromProductIds(listName);
            setSelectedDeals(products);

            // Auto-open sidebar on desktop when list is selected and has deals
            if (!isMobile && products.length > 0) {
                setShowSelectedSidebar(true);
            }
        } catch (error) {
            setSelectedDeals([]);
        }
        handleListMenuClose();
    };

    const handleDealSelect = async (dealId, variantDeal = null) => {
        if (!selectedList) return;

        const isSelected = isDealSelected(dealId);
        let updatedSelectedDeals;

        if (isSelected) {
            // Remove deal from selected deals
            updatedSelectedDeals = selectedDeals.filter(deal => deal.productId !== dealId);
        } else {
            // Add deal to selected deals
            // Use variantDeal if provided (from variants), otherwise find in main deals array
            const dealToAdd = variantDeal || deals.find(deal => deal.productId === dealId);
            if (!dealToAdd) return;
            updatedSelectedDeals = [...selectedDeals, dealToAdd];
        }

        setSelectedDeals(updatedSelectedDeals);

        // Update the list in the backend
        const productIds = updatedSelectedDeals.map(deal => deal.productId);
        const promoCodes = updatedSelectedDeals.map(deal => deal.promoCode);

        await saveCreatorList(selectedList, productIds, promoCodes);

        // Auto-open sidebar when first deal is added (desktop only)
        if (!isMobile && selectedDeals.length === 0 && updatedSelectedDeals.length > 0) {
            setShowSelectedSidebar(true);
        }

        // Auto-close sidebar when all deals are removed (desktop only)
        if (!isMobile && updatedSelectedDeals.length === 0) {
            setShowSelectedSidebar(false);
        }
    };

    const handleSaveNewList = async () => {
        if (!newListName.trim()) return;

        setIsSavingList(true);

        try {
            await saveCreatorList(newListName.trim());

            // Automatically select the newly created list
            setSelectedList(newListName.trim());
            setSelectedDeals([]);

            setNewListName('');
            setIsModalOpen(false);
        } catch (error) {
            // Error handling for list creation
            console.error('Error creating list:', error);
        } finally {
            setIsSavingList(false);
        }
    };

    const handleDeleteList = async (listNameToDelete) => {
        setDeletingListName(listNameToDelete);

        try {
            let res = await PolnApi.request(`/creator/lists/${listNameToDelete}`, {
                method: 'DELETE'
            });

            if (res?.statusCode === 200) {
                await getCreatorList();

                if (selectedList === listNameToDelete) {
                    setSelectedList('');
                    setSelectedDeals([]);
                }
            }
        } catch (error) {
            // Error handling for list deletion
            console.error('Error deleting list:', error);
        } finally {
            setDeletingListName(null);
        }
    };

    const handleViewListToggle = () => {
        if (isMobile) {
            setViewListModal(!viewListModal);
        } else {
            setShowSelectedSidebar(!showSelectedSidebar);
        }
    };

    // Filter lists based on search
    useEffect(() => {
        if (searchList.trim() === '') {
            setFilteredLists(lists);
        } else {
            setFilteredLists(
                lists.filter(listName =>
                    listName.toLowerCase().includes(searchList.toLowerCase())
                )
            );
        }
    }, [searchList, lists]);

    // if (!collections || collections.length === 0) {
    //     return null
    // }

    async function handleShowHtmlCode() {
        try {
            let res = await PolnApi.request(`/kit/plugin/deals`, {
                method: 'POST',
                body: {
                    settings: {
                        list: selectedList
                    }
                }
            });
            if (res?.statusCode === 200) {
                setHtmlCode(res?.body?.html || '');
                setShowHtmlCode(true);
            }
        } catch (error) {
            // Error handling for HTML code generation
        }
    }

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(htmlCode);
            setCopySuccess(true);
        } catch (error) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = htmlCode;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
        }
    };

    const renderCreatorListManagement = () => (
        <div className="list-management" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginTop: '5px',
            marginBottom: '15px',
            padding: isMobile ? '15px 8px 15px 8px' : '15px 16px 15px 16px',
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: isMobile ? 'nowrap' : 'wrap'
        }}>
            <Button
                variant="contained"
                id="create-list-button-tour"
                onClick={() => setIsModalOpen(true)}
                startIcon={<FaPlus size={14} />}
                sx={{
                    bgcolor: 'var(--theme-dark)',
                    color: 'white',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '8px 16px',
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                    },
                    '&:hover': {
                        bgcolor: 'var(--theme-color)',
                        opacity: 0.9,
                    },
                    ...(isMobile ? {
                        width: '60%',
                        padding: '8px 16px',
                        fontSize: '12px',
                        minWidth: 'auto'
                    } : {})
                }}
            >
                CREATE LIST
            </Button>

            <Button
                variant="contained"
                id="choose-list-button-tour"
                onClick={handleListMenuOpen}
                startIcon={<FaPlay size={14} />}
                sx={{
                    bgcolor: 'var(--theme-dark)',
                    color: 'white',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '8px 16px',
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                    },
                    '&:hover': {
                        bgcolor: 'var(--theme-color)',
                        opacity: 0.9
                    },
                    ...(isMobile ? {
                        width: '60%',
                        padding: '8px 16px',
                        fontSize: '12px',
                        minWidth: 'auto'
                    } : {})
                }}
            >
                CHOOSE LIST
            </Button>

            <Button
                variant="contained"
                onClick={handleViewListToggle}
                id="view-hide-list-button-tour"
                startIcon={<FaEye size={14} />}
                disabled={!selectedList || selectedDeals.length === 0}
                sx={{
                    bgcolor: 'var(--theme-dark)',
                    color: 'white',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '8px 16px',
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                    },
                    '&:hover': {
                        bgcolor: 'var(--theme-color)',
                        opacity: 0.9
                    },
                    ...(isMobile ? {
                        width: '60%',
                        padding: '8px 16px',
                        fontSize: '12px',
                        minWidth: 'auto'
                    } : {}),
                    '&.Mui-disabled': {
                        bgcolor: 'var(--theme-dark)',
                        opacity: 0.5,
                        color: 'rgba(255, 255, 255, 0.7)',
                    }
                }}
            >
                {!isMobile && showSelectedSidebar ? 'HIDE LIST' : 'VIEW LIST'}
            </Button>


            <Button
                variant="contained"
                onClick={handleShowHtmlCode}
                startIcon={<FaCode size={14} />}
                id="show-html-list-button-tour"
                disabled={!selectedList || selectedDeals.length === 0}
                sx={{
                    bgcolor: 'var(--theme-dark)',
                    color: 'white',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '8px 16px',
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                    },
                    '&:hover': {
                        bgcolor: 'var(--theme-color)',
                        opacity: 0.9
                    },
                    ...(isMobile ? {
                        width: '60%',
                        padding: '8px 16px',
                        fontSize: '12px',
                        minWidth: 'auto'
                    } : {}),
                    '&.Mui-disabled': {
                        bgcolor: 'var(--theme-dark)',
                        opacity: 0.5,
                        color: 'rgba(255, 255, 255, 0.7)',
                    }
                }}
            >
                Show Html
            </Button>

            <Popover
                id={id}
                open={listMenuOpen}
                anchorEl={anchorEl}
                onClose={handleListMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: isMobile ? '80vw' : '350px',
                        maxHeight: '400px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search lists..."
                    value={searchList}
                    onChange={(e) => setSearchList(e.target.value)}
                    sx={{ m: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <FaSearch />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{
                    overflow: 'auto',
                    flex: 1,
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                    }
                }}>
                    {isListsLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 3
                        }}>
                            <Typography>Loading lists...</Typography>
                        </Box>
                    ) : filteredLists.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 3
                        }}>
                            <Typography>No lists found</Typography>
                        </Box>
                    ) : (
                        filteredLists.map((listItem) => (
                            <MenuItem
                                key={listItem.listName}
                                onClick={() => handleListSelect(listItem.listName)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1,
                                    px: 2,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    backgroundColor: selectedList === listItem.listName ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                                }}
                            >
                                <Typography sx={{
                                    fontSize: '14px',
                                    flex: 1,
                                    marginRight: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {listItem.listName}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={deletingListName === listItem.listName}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteList(listItem.listName);
                                    }}
                                    sx={{
                                        color: 'red',
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                        },
                                        '&.Mui-disabled': {
                                            color: 'rgba(255, 0, 0, 0.5)',
                                        }
                                    }}
                                >
                                    {deletingListName === listItem.listName ? (
                                        <CircularProgress
                                            size={12}
                                            sx={{
                                                color: 'red',
                                            }}
                                        />
                                    ) : (
                                        <FaTrash size={12} />
                                    )}
                                </IconButton>
                            </MenuItem>
                        ))
                    )}
                </Box>
            </Popover>
        </div>
    );

    const renderCreatorModals = () => (
        <>
            <Drawer
                anchor="bottom"
                open={viewListModal}
                onClose={() => setViewListModal(false)}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: isMobile ? '60vh' : '70vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: 3,
                        backgroundColor: 'white',
                    }}
                    role="presentation"
                >
                    <Typography variant="h5" sx={{ marginBottom: 2, textAlign: 'center' }}>
                        Selected Deals in <b>{selectedList}</b>
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'center',
                        }}
                    >
                        {selectedDeals?.map((deal) => (
                            <Box
                                key={`selected-${deal.productId}`}
                                sx={{
                                    width: '100%',
                                    '@media (min-width: 426px)': {
                                        width: '48%',
                                    },
                                    '@media (min-width: 768px)': {
                                        width: '31%',
                                    },
                                    '@media (min-width: 1024px)': {
                                        width: '23%',
                                    },
                                    '@media (min-width: 1200px)': {
                                        width: '18%',
                                    },
                                }}
                            >
                                <SelectedDealCard
                                    deal={deal}
                                    onSelect={handleDealSelect}
                                    isDealSelected={isDealSelected}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Drawer>

            <Modal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setIsSavingList(false);
                }}
                aria-labelledby="add-list-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h2>Add New List</h2>
                    <TextField
                        fullWidth
                        label="List Name"
                        id="create-list-input-button-tour"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        margin="normal"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'var(--theme-dark)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'var(--theme-color)',
                                },
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSaveNewList}
                        id="create-list-input-save-button-tour"
                        disabled={!newListName.trim() || isSavingList}
                        sx={{
                            mt: 2,
                            backgroundColor: 'var(--theme-dark)',
                            '&:hover': {
                                backgroundColor: 'var(--theme-color)',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'var(--theme-dark)',
                                opacity: 0.5,
                                color: 'rgba(255, 255, 255, 0.7)',
                            }
                        }}
                    >
                        {isSavingList ? (
                            <CircularProgress
                                size={20}
                                sx={{
                                    color: 'white',
                                    marginRight: 1
                                }}
                            />
                        ) : null}
                        {isSavingList ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Modal>

            {/* HTML Code Modal */}
            {
                userType === 'creator' && showHtmlCode && (
                    <>
                        <Modal
                            open={showHtmlCode}
                            onClose={() => setShowHtmlCode(false)}
                            aria-labelledby="html-code-modal"
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: isMobile ? '95vw' : '80vw',
                                maxWidth: '900px',
                                height: isMobile ? '80vh' : '70vh',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 3,
                                    borderBottom: '1px solid #e0e0e0',
                                    backgroundColor: 'var(--theme-dark)',
                                    color: 'white'
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                        HTML Code for List "{selectedList}"
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleCopyToClipboard}
                                            startIcon={<FaCopy size={14} />}
                                            sx={{
                                                bgcolor: 'var(--second-color)',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textTransform: 'uppercase',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                padding: '6px 12px',
                                                '&:hover': {
                                                    bgcolor: 'var(--second-color)',
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            Copy
                                        </Button>
                                        <IconButton
                                            onClick={() => setShowHtmlCode(false)}
                                            sx={{
                                                color: 'red',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                                },
                                            }}
                                        >
                                            <FaTimes size={20} />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    p: 0,
                                    backgroundColor: '#f8f9fa'
                                }}>
                                    <Box
                                        component="pre"
                                        sx={{
                                            margin: 0,
                                            padding: 3,
                                            backgroundColor: '#2d3748',
                                            color: '#e2e8f0',
                                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                            fontSize: '13px',
                                            lineHeight: 1.5,
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            height: '100%',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                                height: '8px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                        }}
                                    >
                                        {htmlCode}
                                    </Box>
                                </Box>
                            </Box>
                        </Modal>

                        <Snackbar
                            open={copySuccess}
                            autoHideDuration={3000}
                            onClose={() => setCopySuccess(false)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <Alert
                                onClose={() => setCopySuccess(false)}
                                severity="success"
                                sx={{
                                    backgroundColor: 'var(--theme-dark)',
                                    color: 'white',
                                    '& .MuiAlert-icon': {
                                        color: 'white'
                                    }
                                }}
                            >
                                HTML code copied to clipboard!
                            </Alert>
                        </Snackbar>
                    </>
                )
            }
        </>
    );

    return (
        <div>
            <div
                className="deals-content-wrapper"
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden'
                }}
            >
                <div
                    className="user-deal-item-container"
                    style={{
                        width: '100%',
                        padding: isMobile ? '0 8px' : '0 16px'
                    }}
                >
                    {userType === 'creator' && renderCreatorListManagement()}

                    <UserDeals
                        propDeals={userType === 'creator' ? filteredDeals : deals}
                        deleteDeal={deleteDeal}
                        onViewMore={() => handleViewMore()}
                        showCheckBox={userType === 'creator' && !!selectedList}
                        onDealSelect={userType === 'creator' ? handleDealSelect : undefined}
                        selectedDeals={userType === 'creator' ? selectedDeals : []}
                        showSidebar={userType === 'creator' && showSelectedSidebar && !isMobile}
                        sidebarContent={
                            userType === 'creator' && selectedList && selectedDeals.length > 0 && showSelectedSidebar && !isMobile ? (
                                <Box
                                    sx={{
                                        width: '300px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        padding: 1.5,
                                        maxHeight: '80vh',
                                        overflowY: 'auto',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                    <Typography variant="h6" sx={{
                                        marginBottom: 2,
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        color: 'var(--theme-dark)'
                                    }}>
                                        Selected in <span style={{ color: 'var(--theme-color)' }}>{selectedList}</span>
                                    </Typography>

                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }}>
                                        {selectedDeals.map((deal) => (
                                            <Box key={`sidebar-${deal.productId}`} sx={{ width: '100%' }}>
                                                <SelectedDealCard
                                                    deal={deal}
                                                    onSelect={handleDealSelect}
                                                    isDealSelected={isDealSelected}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : null
                        }
                    />

                    {userType === 'creator' && renderCreatorModals()}
                </div>
            </div>
        </div>
    );
}

// SelectedDealCard component for creator functionality
const SelectedDealCard = ({ deal, onSelect, isDealSelected }) => (
    <Card sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        p: 1
    }}>
        <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}>
            <Checkbox
                checked={isDealSelected(deal.productId)}
                onChange={() => onSelect(deal.productId)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{
                    color: 'var(--theme-dark)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '4px',
                    padding: '4px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-checked': {
                        color: 'var(--theme-dark)',
                    },
                }}
            />
        </Box>
        <CardMedia
            component="img"
            image={deal.image}
            alt={deal.title}
            sx={{
                height: 100,
                objectFit: "contain",
                backgroundColor: "#f5f5f5"
            }}
        />
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="h6" sx={{ fontSize: "14px", mb: 0.5 }}>
                <span style={{ color: 'red', fontWeight: 600 }}>{deal.percentage}% OFF</span>
                {!deal.promoCode?.toUpperCase().includes('KEEPA') && (
                    <>
                        <span style={{ margin: '0 4px', color: '#000' }}>|</span>
                        <span style={{
                            backgroundColor: '#d4f7dc',
                            color: 'rgb(76, 171, 12)',
                            padding: '1px 4px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '12px'
                        }}>
                            PROMO CODE
                        </span>
                    </>
                )}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", color: "red", fontSize: "14px" }}>
                {formatPrice(deal.discountedPrice)}{" "}
                <Typography component="span" sx={{
                    textDecoration: "line-through",
                    color: "text.secondary",
                    fontSize: "12px"
                }}>
                    <b>{formatPrice(deal.price)}</b>
                </Typography>
            </Typography>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5
            }}>
                <Typography variant="caption" color="textSecondary">
                    {formatDealTime(deal.validTill).text}
                </Typography>
                <Typography variant="caption" sx={{ color: "black", fontWeight: "bold" }}>
                    {formatDealTime(deal.validTill).time}
                </Typography>
            </Box>
        </CardContent>
    </Card>
); 